import { ButtonOwnProps } from '#app/components/Buttons/Button.js';
import { SCHEDULE_DEMO_URL } from '#app/constants/demo.js';
import { ClientSubscriptionBillingCycle } from '@contra/graphql-types';
import { ReactNode } from 'react';

export type SubscriptionPlanDetails = {
  readonly ctas?: {
    readonly primary: {
      readonly label: string;
      readonly url?: string;
      readonly variant?: ButtonOwnProps['variant'];
    };
    readonly secondary?: {
      readonly label: string;
      readonly url?: string;
    };
  };
  readonly description: string;
  readonly features: ReactNode[];
  readonly featuresListHeader: string;
  readonly planNid: SubscriptionPlanNid;
  readonly price: SubscriptionPlanPrice;
  readonly title: string;
  readonly variant: SubscriptionPlanVariant;
};

export type SubscriptionPlanNid = 'BUILD' | 'GROW' | 'MEMBERS_ONLY' | 'SCALE';

export type SubscriptionPlanPrice =
  | 'CONTACT_FOR_PRICING'
  | null
  | {
      readonly basePrice?: number;
      readonly billingCycle?: ClientSubscriptionBillingCycle;
      readonly currentPrice: number;
      readonly frequency: SubscriptionPlanFrequency;
      readonly savings?: null | number;
    };

type getSubscriptionPlanDetails = {
  readonly activeSubscription?: boolean;
  readonly billingCycle?: ClientSubscriptionBillingCycle;
  readonly copyOverrides?: {
    readonly [key in SubscriptionPlanNid]?: {
      readonly description: string;
      readonly featuresListHeader: string;
      readonly title: string;
    };
  };
  readonly includePlans?: Array<Partial<SubscriptionPlanNid>>;
  readonly membersOnly?: boolean;
  readonly priceAmountOverride?: {
    readonly [key in SubscriptionPlanNid]?: {
      readonly basePrice?: number;
      readonly currentPrice: number;
      readonly savings?: number;
    };
  };
  readonly usageMetersOverride?: {
    readonly [key in SubscriptionPlanNid]?: {
      readonly contractFees?: {
        readonly featureLineItem: ReactNode;
      };
      readonly jobBoosts?: {
        readonly featureLineItem: ReactNode;
      };
    };
  };
};

type SubscriptionPlanFrequency = 'month' | 'yearly';

type SubscriptionPlanVariant =
  | 'bestValue'
  | 'default'
  | 'mostPopular'
  | 'oneTimeOffer';

export const getSubscriptionPlanDetails = ({
  activeSubscription,
  billingCycle,
  copyOverrides = {},
  includePlans = ['BUILD', 'GROW', 'SCALE', 'MEMBERS_ONLY'],
  membersOnly,
  priceAmountOverride = {},
  usageMetersOverride,
}: getSubscriptionPlanDetails = {}): Record<
  Partial<SubscriptionPlanNid>,
  SubscriptionPlanDetails
> => {
  const buildPlanJobBoostsFeatureLineItem =
    usageMetersOverride?.BUILD?.jobBoosts?.featureLineItem ??
    '1 free boosted job per month ($199 value)';

  const membersOnlyPlanJobBoostsFeatureLineItem =
    usageMetersOverride?.MEMBERS_ONLY?.jobBoosts?.featureLineItem ??
    '1 free boosted job per month ($199 value)';

  const growPlanJobBoostsFeatureLineItem =
    usageMetersOverride?.GROW?.jobBoosts?.featureLineItem ??
    '2 free boosted jobs per month ($398 value)';
  const scalePlanJobBoostsFeatureLineItem =
    usageMetersOverride?.SCALE?.jobBoosts?.featureLineItem ??
    '3 free boosted jobs per month ($597 value)';

  const buildPlanContractFeesFeatureLineItem =
    usageMetersOverride?.BUILD?.contractFees?.featureLineItem ??
    '5 free contracts per month ($145 value)';

  const membersOnlyPlanContractFeesFeatureLineItem =
    usageMetersOverride?.MEMBERS_ONLY?.contractFees?.featureLineItem ??
    '5 free contracts per month ($145 value)';

  const growPlanContractFeesFeatureLineItem =
    usageMetersOverride?.GROW?.contractFees?.featureLineItem ??
    '15 free contracts per month ($435 value)';
  const scalePlanContractFeesFeatureLineItem =
    usageMetersOverride?.SCALE?.contractFees?.featureLineItem ??
    '25 free contracts per month ($725 value)';

  const allPlansContent = {
    BUILD: {
      ctas: {
        primary: {
          label: 'Upgrade',
          variant: 'V2Secondary',
        },
        secondary: {
          label: 'Schedule a demo',
          url: SCHEDULE_DEMO_URL,
        },
      },
      description: 'For sourcing & hiring flexible teams',
      features: [
        buildPlanContractFeesFeatureLineItem,
        buildPlanJobBoostsFeatureLineItem,
        'Manage freelancers in a shared network',
        'Find talent faster with advanced discover tools',
      ],
      featuresListHeader: 'Everything you love, plus:',
      planNid: 'BUILD',
      price: {
        basePrice: priceAmountOverride.BUILD?.basePrice ?? 199,
        billingCycle,
        currentPrice: priceAmountOverride.BUILD?.currentPrice ?? 199,
        frequency: 'month',
        savings: priceAmountOverride.BUILD?.savings ?? null,
      },
      title: 'Build',
      variant: 'default',
    },
    GROW: {
      ctas: {
        primary: {
          label: 'Upgrade',
          variant: 'V2Secondary',
        },
        secondary: {
          label: 'Schedule a demo',
          url: SCHEDULE_DEMO_URL,
        },
      },
      description: 'For a complete management system',
      features: [
        growPlanContractFeesFeatureLineItem,
        growPlanJobBoostsFeatureLineItem,
        'Finance integration',
        'Insights & analytics',
        'Shareable talent list & CRM',
        'Document management (NDA, MSA)',
        'White glove onboarding',
      ],
      featuresListHeader: 'Everything in Build, plus:',
      planNid: 'GROW',
      price: {
        basePrice: priceAmountOverride.GROW?.basePrice ?? 499,
        billingCycle,
        currentPrice: priceAmountOverride.GROW?.currentPrice ?? 499,
        frequency: 'month',
        savings: priceAmountOverride.GROW?.savings ?? null,
      },
      title: 'Grow',
      variant: 'bestValue',
    },
    MEMBERS_ONLY: {
      ctas: {
        primary: {
          label: 'Upgrade',
          variant: 'V2Secondary',
        },
        secondary: {
          label: 'Schedule a demo',
          url: SCHEDULE_DEMO_URL,
        },
      },
      description:
        copyOverrides.MEMBERS_ONLY?.description ||
        copyOverrides.MEMBERS_ONLY?.description === ''
          ? copyOverrides.MEMBERS_ONLY?.description
          : membersOnly
            ? 'Access the world’s best talent network and future proof your talent strategy'
            : 'For sourcing & hiring flexible teams',
      features: membersOnly
        ? activeSubscription
          ? [
              membersOnlyPlanJobBoostsFeatureLineItem,
              `Exclusive access to 800,000+ of the world’s best independent professionals`,
              `Payments, contracts and compliance tools to manage your global network, all in one place `,
              `Dedicated recruiting specialist, so you can find the perfect person for every project`,
              `Priority support from the Contra team`,
            ]
          : [
              `Exclusive access to 800,000+ of the world’s best independent professionals`,
              `Payments, contracts and compliance tools to manage your global network, all in one place `,
              `Dedicated recruiting specialist, so you can find the perfect person for every project`,
              `Priority support from the Contra team`,
            ]
        : [
            membersOnlyPlanContractFeesFeatureLineItem,
            membersOnlyPlanJobBoostsFeatureLineItem,
            'Manage freelancers in a shared network',
            'Find talent faster with advanced discover tools',
          ],
      featuresListHeader:
        copyOverrides.MEMBERS_ONLY?.featuresListHeader ??
        'Everything you love, plus:',
      planNid: 'MEMBERS_ONLY',
      price: {
        basePrice: priceAmountOverride.MEMBERS_ONLY?.basePrice ?? 999,
        billingCycle,
        currentPrice: priceAmountOverride.MEMBERS_ONLY?.currentPrice ?? 999,
        frequency: billingCycle === 'MONTHLY' ? 'month' : 'yearly',
        savings: priceAmountOverride.MEMBERS_ONLY?.savings ?? null,
      },
      title: copyOverrides.MEMBERS_ONLY?.title ?? 'Members-only network',
      variant: 'default',
    },
    SCALE: {
      ctas: {
        primary: {
          label: 'Upgrade',
          variant: 'V2Secondary',
        },
        secondary: {
          label: 'Schedule a demo',
          url: SCHEDULE_DEMO_URL,
        },
      },
      description: 'For custom support',
      features: [
        scalePlanContractFeesFeatureLineItem,
        scalePlanJobBoostsFeatureLineItem,
        'Advanced integrations',
        'Team permissions management',
        'Approval workflows',
        'Dedicated sourcing specialist',
        'Customer service SLA',
      ],
      featuresListHeader: 'Everything in Grow, plus:',
      planNid: 'SCALE',
      price: {
        basePrice: priceAmountOverride.SCALE?.basePrice ?? 999,
        billingCycle,
        currentPrice: priceAmountOverride.SCALE?.currentPrice ?? 999,
        frequency: 'month',
        savings: priceAmountOverride.SCALE?.savings ?? null,
      },
      title: 'Scale',
      variant: 'default',
    },
  };

  return includePlans.reduce(
    (accumulator, planNid) => {
      return {
        ...accumulator,
        [planNid]: allPlansContent[planNid],
      };
    },
    {} as Record<Partial<SubscriptionPlanNid>, SubscriptionPlanDetails>,
  );
};
